/**
 * ForgotPasswordPage — TREATX-AUTH-PASSWORD-RESET-SELFSERVE-01
 * ============================================================
 *
 * Three-step self-service password reset that reuses the existing
 * EmailOtpVerificationService:
 *   1. Email     → POST /api/auth/password-reset/request
 *   2. OTP       → enter 6-digit code (uses shared <EmailOtpStep>)
 *   3. Password  → POST /api/auth/password-reset/complete
 *
 * No login session required. Existing JWTs for the resetting user are
 * invalidated server-side via `password_changed_at` rotation.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { EmailOtpStep } from '../components/EmailOtpStep';
import { Mail, Lock, KeyRound, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const extract = (err, key) => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail[key]) return detail[key];
  if (typeof detail === 'string' && key === 'message') return detail;
  const respMsg = err?.response?.data?.message;
  if (typeof respMsg === 'string') {
    const re = key === 'code' ? /'code'\s*:\s*'([a-zA-Z0-9_]+)'/ : /'message'\s*:\s*'([^']+)'/;
    const m = respMsg.match(re);
    if (m) return m[1];
  }
  return null;
};

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState('email'); // email | otp | password | success
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(600);
  const [otpResendAfter, setOtpResendAfter] = useState(60);
  const [otpError, setOtpError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const requestOtp = async () => {
    const r = await axios.post(`${API_URL}/api/auth/password-reset/request`, { email });
    const d = r.data || {};
    setOtpExpiresIn(Number(d.expires_in) || 600);
    setOtpResendAfter(Number(d.resend_after) || 60);
    return d;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setLoading(true); setError('');
    try {
      await requestOtp();
      setStep('otp');
    } catch (err) {
      const code = extract(err, 'code');
      const msg = extract(err, 'message') || 'Could not send the reset code. Please try again.';
      if (code === 'otp_cooldown_active') {
        setError(msg);
      } else {
        // Backend always returns success-shaped 200 for unknown emails (no enumeration),
        // so a non-200 here is genuinely a server problem worth showing.
        setError(msg);
      }
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    setOtpError('');
    try {
      await requestOtp();
    } catch (err) {
      setOtpError(extract(err, 'message') || 'Resend failed. Please wait a moment and try again.');
    }
  };

  // OTP step → store code, move to password step.
  // We don't VERIFY the code yet — we verify+rotate atomically in step 3
  // via /password-reset/complete (which mark-uses the OTP server-side).
  const handleOtpEntered = (code) => {
    setOtpCode(code);
    setOtpError('');
    setStep('password');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/complete`, {
        email,
        otp_code: otpCode,
        new_password: password,
      });
      setStep('success');
    } catch (err) {
      const code = extract(err, 'code');
      const msg = extract(err, 'message') || 'Could not reset password. Please try again.';
      const otpCodes = new Set([
        'otp_invalid', 'otp_expired', 'otp_not_found',
        'otp_too_many_attempts', 'otp_required', 'otp_invalid_format',
      ]);
      if (otpCodes.has(code)) {
        // Bounce back to OTP step — code was rejected.
        setOtpError(msg);
        setStep('otp');
      } else {
        setError(msg);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary/5 flex items-center justify-center px-4 py-8" data-testid="forgot-password-page">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
            <KeyRound className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-xl font-bold text-slate-800">{t('patient.auth.forgotTitle')}</h1>
          {step === 'email' && (
            <p className="text-sm text-slate-500 mt-1">
              {t('patient.auth.forgotBody')}
            </p>
          )}
        </CardHeader>

        <CardContent>
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-4" data-testid="forgot-email-form">
              <div>
                <Label htmlFor="email">{t('patient.auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-send-code-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('patient.auth.forgotSubmit')}
              </Button>
              <Link to="/login" className="flex items-center justify-center text-xs text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> {t('patient.common.back')}
              </Link>
            </form>
          )}

          {step === 'otp' && (
            <EmailOtpStep
              email={email}
              cooldownSeconds={otpResendAfter}
              expiresInSeconds={otpExpiresIn}
              loading={loading}
              errorMsg={otpError}
              onVerify={handleOtpEntered}
              onResend={handleResendOtp}
              onBack={() => { setStep('email'); setOtpError(''); }}
              testIdPrefix="forgot-otp"
              // TREATX-P1-OTP-03 — anti-enumeration-safe copy. The
              // backend always returns HTTP 200 to /password-reset/request
              // even when the account does not exist (by design), so the
              // frontend must NOT claim an email was actually sent.
              // TREATX-P1-OTP-04 — Do NOT echo the entered email back at
              // the user (privacy / shoulder-surfing). The user just
              // typed it; the generic phrase "this email address" is
              // strictly less information at zero readability cost.
              description={(
                <>
                  If a TreatX account exists for this email address, a
                  verification code will arrive shortly. Please also check
                  your <em>Spam</em> or <em>Junk</em> folder.
                </>
              )}
            />
          )}

          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="newpwd">{t('patient.auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="newpwd"
                    type="password"
                    placeholder="Min 6 characters, letters + numbers"
                    className="pl-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    data-testid="forgot-newpwd"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="confirmpwd">{t('patient.auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmpwd"
                    type="password"
                    placeholder="Re-enter password"
                    className="pl-9"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    data-testid="forgot-confirmpwd"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="forgot-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || !password || !confirm} data-testid="forgot-set-password-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('patient.auth.resetSubmit')}
              </Button>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center space-y-4 py-4" data-testid="forgot-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800">{t('patient.auth.resetSuccess').split('.')[0]}</h2>
                <p className="text-sm text-slate-600 mt-1">
                  {t('patient.auth.resetSuccess')}
                </p>
              </div>
              <Button onClick={() => navigate('/login')} className="w-full" data-testid="forgot-go-to-login">
                {t('patient.auth.signIn')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
